import { useState } from "react"
import { DataTypeList, Labels, useDataTypesHooks } from "../../../../../features/data-types"
import { PanelWrapper, Column, Data, FieldType, FieldValue, SpinnerDiv, UIPermission, Badge } from "../../../../../features/ui"
import { BookmarkButton } from "../../../../../features/bookmarks"
import { RoutePath } from "../../../../../features/routes"
import { DataType } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { PaymentCategory, PaymentType } from "../../../state/types"
import { PaymentTypeFilters } from "./paymentTypeFilters"
import { define } from "../../../../../utils/typeUtils"

export const PaymentTypeList = () => {
    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    
    const { formatValue } = useDataTypesHooks()
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PAYMENT_TYPES)

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'category',
            type: FieldType.TEXT,
            label: 'Tipo',
            sortValue: (value?: FieldValue) => Labels.paymentCategoryLabel(value as PaymentCategory),
            render: (value?: FieldValue) => <Badge label={Labels.paymentCategoryLabel(define(value) as PaymentCategory)} />
        },
        {
            name: 'yield',
            type: FieldType.PERCENTAGE,
            label: 'Ajuste',
            render: (value?: FieldValue) => {
                const paymentTypeYield = value as number
                const absYield = Math.abs(paymentTypeYield)
                return <>{formatValue(absYield, FieldType.NUMBER)}% de {paymentTypeYield <= 0 ? 'Descuento' : 'Recargo'}</>
            }
        },
        {
            name: 'enabled',
            type: FieldType.TEXT,
            label: 'Habilitado',
            render: (value?: FieldValue) => value ?
                <Badge label="Habilitado" kind="secondary" icon="check" small /> :
                <Badge label="Deshabilitado" kind="primary" icon="slash-circle" small />
        }
    ]

    const toRow = (paymentType: PaymentType) => {
        const { cashFundIds, ...paymentTypeProps } = paymentType
        return { ...paymentTypeProps, enabled: !paymentType.disabled }
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        const paymentTypes = dataTypes as PaymentType[]
        setRows(paymentTypes.map(toRow))
        setAsyncDataLoaded(true)
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    const isEditable = (id?: string) => {
        const row = rows.find(row => row.id === id)
        return row?.category !== PaymentCategory.CREDIT_ACCOUNT
    }

    return (
        <PanelWrapper title="Medios de Pago" titleRight={<BookmarkButton routePath={RoutePath.PAYMENT_TYPES} />}>
            <PaymentTypeFilters onSearchStart={onSearchStart} onSearchDone={onSearchDone}/>
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    readOnly={readOnly}
                    minItems={1}
                    isEditable={isEditable}
                />
            )}
        </PanelWrapper>
    )
}
